import { Link } from "react-router-dom";
import { Waves, MapPin, MessageSquare } from "lucide-react";
import { CONTACT } from "../lib/assets";
import { openCookieSettings } from "./CookieConsent";

const SERVICE_LINKS = [
  ["Depth / Bathymetric Mapping", "/services#bathymetric"],
  ["Aquatic Vegetation Mapping", "/services"],
  ["Bottom Hardness Mapping", "/services"],
  ["Fish & Habitat Mapping", "/services"],
  ["GIS Map Services", "/services"],
];

const COMPANY_LINKS = [
  ["Solutions", "/solutions"],
  ["How It Works", "/how-it-works"],
  ["About Us", "/about"],
  ["Service Areas (US & Canada)", "/locations"],
  ["Request a Quote", "/request-a-quote"],
];

const LEGAL_LINKS = [
  ["Privacy Policy", "/privacy-policy"],
  ["Terms of Service", "/terms-of-service"],
  ["Cookie Policy", "/cookie-policy"],
  ["Accessibility", "/accessibility"],
];

const LinkColumn = ({ title, links }) => (
  <div>
    <h4 className="font-mono text-xs uppercase tracking-[0.2em] text-cyan-400 mb-4">{title}</h4>
    <ul className="space-y-2.5 text-base">
      {links.map(([label, to]) => (
        <li key={label}>
          <Link to={to} className="text-slate-300 hover:text-cyan-300 transition-colors">{label}</Link>
        </li>
      ))}
    </ul>
  </div>
);

const Brand = () => (
  <div className="lg:col-span-1">
    <div className="flex items-center gap-2.5 mb-4">
      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-cyan-500/20 to-teal-500/10 border border-cyan-500/30 flex items-center justify-center">
        <Waves className="w-4 h-4 text-cyan-400" />
      </div>
      <span className="font-bold text-white">Lake Monitor AI</span>
    </div>
    <p className="text-base text-slate-300 leading-relaxed">
      Full-service bathymetric and aquatic mapping. We collect the sonar data and deliver finished,
      decision-ready maps and reports.
    </p>
  </div>
);

const ContactColumn = () => (
  <div>
    <h4 className="font-mono text-xs uppercase tracking-[0.2em] text-cyan-400 mb-4">Contact</h4>
    <p className="flex items-start gap-3 text-base text-slate-300">
      <MapPin className="w-4 h-4 text-cyan-400 mt-1 shrink-0" />
      <span>Serving {CONTACT.serviceArea}</span>
    </p>
    <p className="mt-3 text-base text-slate-300 leading-relaxed">
      Tell us about your waterbody and we'll reply with a tailored survey plan and quote.
    </p>
    <Link
      to="/request-a-quote"
      data-testid="footer-contact-us-button"
      className="mt-5 inline-flex items-center gap-2 bg-gradient-to-r from-cyan-400 to-teal-400 hover:from-cyan-300 hover:to-teal-300 text-slate-950 font-bold px-5 py-3 rounded-xl shadow-[0_0_20px_rgba(0,240,255,0.25)] transition-all hover:-translate-y-0.5"
    >
      <MessageSquare className="w-4 h-4" /> Contact Us
    </Link>
  </div>
);

const LegalBar = () => (
  <div className="mt-12 pt-8 border-t border-slate-800 flex flex-col lg:flex-row items-center justify-between gap-4">
    <p className="text-sm text-slate-400">© {new Date().getFullYear()} Lake Monitor AI. All rights reserved.</p>
    <nav aria-label="Legal" className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm" data-testid="footer-legal-nav">
      {LEGAL_LINKS.map(([label, to]) => (
        <Link key={to} to={to} className="text-slate-400 hover:text-cyan-300 transition-colors">{label}</Link>
      ))}
      <button type="button" onClick={openCookieSettings} data-testid="footer-cookie-settings" className="text-slate-400 hover:text-cyan-300 transition-colors">Cookie settings</button>
    </nav>
  </div>
);

export const Footer = () => (
  <footer data-testid="site-footer" className="relative border-t border-slate-800 bg-[#080C18]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
        <Brand />
        <LinkColumn title="Services" links={SERVICE_LINKS} />
        <LinkColumn title="Company" links={COMPANY_LINKS} />
        <ContactColumn />
      </div>
      <LegalBar />
    </div>
  </footer>
);
